import dayjs, { Dayjs } from "dayjs";
import { DATE_ISO, MS_PER_DAY } from "./constants";
import { dataValidation } from "../form/validation-attributes";

export interface CalendarSheet {
    year: number;
    month: number;
    monthName: string;
    days: string[];
    dayNames: string[];
}

const getCalendarSheetStartDate = (year: number, month: number, firstWeekday: number = 1): Dayjs => {
    const firstWeekdayFixed = firstWeekday;
    const firstDayOfMonth = dayjs().year(year).month(month).date(1);
    const startDate = firstDayOfMonth.clone().day(firstWeekdayFixed);
    return startDate.valueOf() > firstDayOfMonth.valueOf() ? startDate.subtract(7, "day") : startDate;
};

const getCalendarSheetEndDate = (year: number, month: number, firstWeekday: number = 1): Dayjs => {
    const lastWeekday = (firstWeekday + 6) % 7;
    let lastDayOfMonth = dayjs().year(year).month(month).date(1);
    lastDayOfMonth = lastDayOfMonth.date(lastDayOfMonth.daysInMonth());
    const endDate = lastDayOfMonth.clone().day(lastWeekday);
    return endDate.valueOf() < lastDayOfMonth.valueOf() ? endDate.add(7, "day") : endDate;
};

export const getCalendarSheet = (year: number, month: number, firstWeekday: number = 1, language?: string): CalendarSheet => {
    if (typeof year !== "number" || typeof month !== "number") {
        // @ts-expect-error Check for typing
        return;
    }
    const lng = language !== undefined && language.length >= 2 ? language : "";
    const startDate = dayjs(getCalendarSheetStartDate(year, month, firstWeekday).format(DATE_ISO));
    const endDate = dayjs(getCalendarSheetEndDate(year, month, firstWeekday).format(DATE_ISO));
    const numDays = Math.round((endDate.valueOf() - startDate.valueOf()) / MS_PER_DAY);
    const numDaysTotal = 7 * Math.ceil((numDays + 1) / 7);

    const days = [];
    for (let i = 0; i < numDaysTotal; i++) {
        days.push(startDate.add(i, "day").format(DATE_ISO));
    }

    // @ts-expect-error Check for typing
    const monthName = (lng !== "" ? dayjs().month(month).locale(language) : dayjs().month(month)).format("MMM");
    const dayNames: string[] = days.slice(0, 7).map((date) => {
        return (lng !== "" ? dayjs(date).locale(lng) : dayjs(date)).format("dd");
    });

    return {
        year,
        month,
        monthName,
        days,
        dayNames
    };
};

export const isDateAfter = (date: string | Dayjs, compareDate: string | Dayjs, acceptSameDay: boolean = false): boolean => {
    if (typeof compareDate === "undefined") {
        return false;
    }
    const diff = dayjs(date).diff(compareDate, "d");
    return acceptSameDay ? diff >= 0 : diff > 0;
};

export const isDateBefore = (date: string | Dayjs, compareDate: string | Dayjs, acceptSameDay: boolean = false): boolean => {
    if (typeof compareDate === "undefined") {
        return false;
    }
    const diff = dayjs(date).diff(compareDate, "d");
    return acceptSameDay ? diff <= 0 : diff < 0;
};

export const isSameDay = (date: string | Dayjs, compareDate: string | Dayjs): boolean => {
    if (typeof date === "undefined") {
        return false;
    }
    if (typeof compareDate === "undefined") {
        return false;
    }
    return dayjs(date).diff(compareDate, "d") === 0;
};

export const restrictDateToRange = (date: string | Dayjs, minDate?: string | Dayjs, maxDate?: string | Dayjs): Dayjs => {
    let d = dayjs(date);
    if (typeof minDate !== "undefined") {
        const dMin = dayjs(minDate);
        if (dMin.isValid() && d.valueOf() < dMin.valueOf()) {
            d = dMin;
        }
    }
    if (typeof maxDate !== "undefined") {
        const dMax = dayjs(maxDate);
        if (dMax.isValid() && d.valueOf() > dMax.valueOf()) {
            d = dMax;
        }
    }
    return d;
};

export const getInvalidErrorMsg = (element: HTMLUxDatePickerElement) => {
    return element.getAttribute(dataValidation.invalid.msg);
};
